import moment from 'moment-timezone';
import PerformanceUtils from 'services/PerformanceUtils';
import MiscUtils from 'services/MiscUtils';
import StaffUtils from 'services/StaffUtils';
import AdminUtils from 'services/AdminUtils';
import PatientUtils from 'services/PatientUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import { getTenantIdentity } from 'hooks/useTenantIdentity';
import {
  PatientDocument,
  Admin,
  Physician,
  Patient,
  DocumentTemplateCategory,
  DocumentStatusHistory,
} from 'generated/api';

interface DocumentTemplate {
  category_id?: string;
  [key: string]: any;
}

export interface DocumentHistoryEntry {
  byWhom: string;
  date: string;
  actionLabel: string;
  // Extra from -> to context (e.g. old title -> new title) derived by diffing
  // against the previous entry's snapshot. Empty for actions with no detail.
  details: string[];
}

// Human-readable labels for DocumentStatusHistory.action values.
const DOCUMENT_HISTORY_ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  approved: 'Approved',
  unapproved: 'Approval removed',
  patient_changed: 'Moved to another patient',
  physician_changed: 'Practitioner changed',
  details_changed: 'Title/type edited',
  hidden: 'Hidden from patient',
  unhidden: 'Shown to patient',
  archived: 'Archived',
  unarchived: 'Unarchived',
  read: 'Viewed by patient',
  filled: 'Filled by patient',
};

class DocumentUtils {
  OPTIONS_FILL = {
    nothing: 'Nothing',
    task: 'Create Task for Admins',
    approve: 'Document should be approved',
  } as const;

  FAX_STATUS = {
    failed: 'SendingFailed',
    queued: 'Queued',
    sent: 'Sent',
    submitted: 'Submitted',
  } as const;

  DOCUMENT_FROM_OPTIONS = [
    'Care& Family Health',
    'External',
    'True North Imaging',
    'Charm Fertility Clinic',
    'St. Michael\u2019s Hospital',
    'UHN',
    'Sunnybrook',
  ] as const;

  // Server-stamped source of HL7-imported Dynacare lab results (never one of the
  // manually selectable DOCUMENT_FROM_OPTIONS above), so `from === 'Dynacare'`
  // is a collision-free discriminant for a Dynacare-sourced document.
  DOCUMENT_SOURCE = {
    dynacare: 'Dynacare',
  } as const;

  DOCUMENT_TYPE = {
    diagnostic: 'Diagnostic Result',
    diagnostic_requisition: 'Diagnostic Requisition',
    consult_note: 'Consult Note',
    pharmacy_authorization: 'Pharmacy Authorization Request',
    demographics: 'Demographics',
    immunization_record: 'Immunization Record',
    appointment_notification: 'Appointment Notification',
    historical_records: 'Historical Records',
    referral: 'Referral',
    incoming_referral: 'Incoming Referral',
    template: 'Template',
    other: 'Other',
    export: 'Export',
  } as const;

  DOCUMENT_TITLES = {
    [this.DOCUMENT_TYPE.diagnostic!]: [
      'Hematology/Microbiology',
      'Pap',
      'HPV',
      'HIV',
      'ECG',
      'COVID-19 Result',
      'Imaging - Specialized Test',
      'Imaging - Ultrasound',
      'Imaging - Xray',
      'Imaging - MRI',
      'Imaging - CT',
      'Mammogram',
      'Colonoscopy',
      'Blood Type',
      'Fecal Immunochemical Test (FIT)',
      'Bone Mineral Density',
    ],
    [this.DOCUMENT_TYPE.consult_note!]: ['Specialty: X and Physician/provider: X'],
    [this.DOCUMENT_TYPE.pharmacy_authorization!]: ['Pharmacy Authorization Request'],
    [this.DOCUMENT_TYPE.demographics!]: [
      'Patient Driver\u2019s License',
      'Permanent Residency Card',
      'Patient Passport',
      'Patient Health Card',
      'Patient Refugee Status',
    ],
    [this.DOCUMENT_TYPE.immunization_record!]: ['Immunization Record', 'Proof of Immunization'],
    [this.DOCUMENT_TYPE.appointment_notification!]: [
      'Appointment Notification',
      'No-Show for Appointment',
      'Cancelled Appointment',
    ],
    [this.DOCUMENT_TYPE.referral!]: [
      'Referral Acknowledgment',
      'Additional Information Required for Referral',
      'Declined Referral',
    ],
  } as const;

  getTemplateMatchingDocumentCategory(
    template: DocumentTemplate | undefined,
    templateCategories: DocumentTemplateCategory[],
  ): string | undefined {
    if (!template) return;
    let category = templateCategories.find(({ id }) => template?.category_id === id)?.name;
    if (category === 'Referrals') return this.DOCUMENT_TYPE.referral;
    if (category === 'Requisitions') return this.DOCUMENT_TYPE.diagnostic_requisition;
  }

  isAssigned(document: PatientDocument): boolean {
    return !!document.patient_id && !!document.physician_id && !!document.document_type;
  }

  isInboundFax(document: PatientDocument): boolean {
    return !!document.fax_id || document.route === 'Fax';
  }

  isDynacare(document: Pick<PatientDocument, 'from'>): boolean {
    return document.from === this.DOCUMENT_SOURCE.dynacare;
  }

  /**
   * Documents whose patient is assigned by hand (because auto-matching failed
   * or they were faxed in) should NOT auto-fill the patient's primary
   * physician on that manual assignment. Two cases:
   *  - Faxed-in diagnostics (e.g. Dynacare rough copies) duplicate results the
   *    provider already received electronically.
   *  - Dynacare HL7 imports that failed to auto-match: an admin assigns the
   *    patient manually, and the NP is then chosen deliberately rather than
   *    defaulted to the primary — otherwise the lab silently lands in the wrong
   *    provider's pending queue.
   * In both cases the NP is left unset for explicit selection. A Dynacare
   * result that auto-matched at ingest already carries its physician_id and is
   * untouched, since this only gates the client-side primary-physician default.
   */
  shouldSkipPrimaryPhysicianAutofill(document: PatientDocument): boolean {
    const isFaxedDiagnostic =
      this.isInboundFax(document) && document.document_type === this.DOCUMENT_TYPE.diagnostic;
    return isFaxedDiagnostic || this.isDynacare(document);
  }

  isLocked(document: Pick<PatientDocument, 'date'>): boolean {
    if (!document?.date) return false;
    return document.date > moment().endOf('day').unix();
  }

  isLockedForPatient(document: Pick<PatientDocument, 'date'>, role: string | undefined): boolean {
    return role === 'patient' && this.isLocked(document);
  }

  getUnlockDateText(document: Pick<PatientDocument, 'date'>): string {
    if (!document?.date) return '';
    return moment.unix(document.date).format('Do MMM YYYY');
  }

  diagnosticTestsMatch(
    a: Pick<PatientDocument, 'results_text'>,
    b: Pick<PatientDocument, 'results_text'>,
  ): boolean {
    // Names are compared with punctuation/whitespace stripped so OCR and HL7
    // variants of the same test line up ("non-hdl-cholesterol(calc" vs
    // "non-hdl-cholesterol (calc)").
    const testNames = (doc: Pick<PatientDocument, 'results_text'>) =>
      new Set(
        (doc.results_text || [])
          .map((line) => line.test?.toLowerCase().replace(/[^a-z0-9]/g, ''))
          .filter((test): test is string => !!test),
      );
    const namesA = testNames(a);
    const namesB = testNames(b);
    if (namesA.size === 0 || namesA.size !== namesB.size) return false;

    // Exact matches first; whatever is left tries to pair up by substring
    // inclusion either way, so panel-prefixed names ("rbc indices: mcv")
    // still match their bare counterpart ("mcv").
    const leftoversA = [...namesA].filter((test) => !namesB.delete(test));
    const leftoversB = [...namesB];
    return leftoversA.every((testA) => {
      const i = leftoversB.findIndex((testB) => testA.includes(testB) || testB.includes(testA));
      if (i === -1) return false;
      leftoversB.splice(i, 1);
      return true;
    });
  }

  getRecentDuplicateDiagnostics(
    documents: PatientDocument[] | undefined,
    currentDoc: Pick<PatientDocument, 'id'>,
    nowUnix: number = moment().unix(),
  ): PatientDocument[] {
    const cutoff = nowUnix - 7 * 24 * 60 * 60; // date is unix seconds (see isLocked)
    return (documents || [])
      .filter(
        (doc) =>
          doc.id !== currentDoc.id &&
          doc.document_type === this.DOCUMENT_TYPE.diagnostic &&
          !doc.archived &&
          (doc.date ?? 0) >= cutoff,
      )
      .sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  }

  filter(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    type?: string,
    targetStatus?: string,
  ): PatientDocument[] {
    return this.toArray(documents).filter(({ document_type, status }) => {
      if (type !== undefined && type !== document_type) return false;
      return targetStatus === undefined || status === targetStatus;
    });
  }

  filterArchived(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    value: boolean,
  ): PatientDocument[] {
    return this.toArray(documents).filter((document) => {
      let isArchived = document.archived || false;
      return isArchived === value;
    });
  }

  filterAssigned(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    assigned: boolean,
  ): PatientDocument[] {
    return this.toArray(documents).filter(({ patient_id }) =>
      assigned ? patient_id !== undefined : patient_id === undefined,
    );
  }

  filterOthers(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    assigned: boolean,
  ): PatientDocument[] {
    return this.filterAssigned(documents, assigned).filter((document) => {
      return (
        document.document_type !== this.DOCUMENT_TYPE.diagnostic &&
        document.document_type !== this.DOCUMENT_TYPE.referral
      );
    });
  }

  filterPatient(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    patientId: string,
  ): PatientDocument[] {
    return this.toArray(documents).filter((document) => {
      return document.patient_id === patientId;
    });
  }

  toArray(documents: PatientDocument[] | Record<string, PatientDocument> = []): PatientDocument[] {
    if (Array.isArray(documents)) {
      return order(documents.filter((item) => item.id !== undefined));
    } else {
      return order(Object.values(documents).filter((item) => item.id !== undefined));
    }
  }

  getFlagsAndCommentsAlert(
    document: PatientDocument,
    role: string = '',
    flagsText: string = '',
    physicians: Record<string, Physician>,
    admins: Record<string, Admin>,
  ): string {
    let text = flagsText;

    if (document.comments) {
      text += `${text ? '\n\n' : ''}Notes for Patient:\n${document.comments}`;
    }
    if (role !== 'patient' && (document as any).general_comments?.length > 0) {
      const assistant = AdminUtils.getAssistantUser(admins);
      let comments = (document as any).general_comments
        .filter((comment: any) => comment.author_id !== assistant?.id)
        .map((comment: any) => {
          const authorId = comment.author_id;
          const staff = physicians[authorId] || admins[authorId];
          const name = staff ? StaffUtils.getName(staff) : authorId;
          return `${name}: ${comment.text}`;
        })
        .join('\n\n');
      if (comments) text += `${text ? '\n\n' : ''}Private Staff Comments:\n${comments}`;
    }

    return text;
  }

  // Structured view of a document's server-authored status_history (staff-only;
  // the backend strips the field from patient responses). Mirrors
  // AppointmentUtils.getStatusHistoryEntries: one entry per record, resolving
  // the actor to a display name and diffing consecutive snapshots for the
  // from -> to context on practitioner/details changes. Array order is
  // authoritative (entries within one write share the same date) — never
  // re-sort by timestamp.
  getStatusHistoryEntries(
    document: PatientDocument,
    physicians: Record<string, Physician> = {},
    admins: Record<string, Admin> = {},
    patient?: Patient,
  ): DocumentHistoryEntry[] {
    const history = (document?.status_history || []).filter((item) => !!item.date);

    return history.map((item, index) => {
      const { by_role = '', by_id, action = '', date } = item;
      const prev = index > 0 ? history[index - 1] : undefined;

      let byWhom: string;
      if (by_role.toLowerCase() === 'system') {
        byWhom = `${getTenantIdentity().nameShort} System`;
      } else if (by_role !== 'patient') {
        const staff = (by_id && physicians[by_id]) || (by_id && admins[by_id]);
        byWhom = staff
          ? `${StaffUtils.getName(staff)} (Staff)`
          : by_role
          ? by_role.charAt(0).toUpperCase() + by_role.slice(1)
          : 'Staff';
      } else {
        byWhom = patient ? `${PatientUtils.getShortName(patient)} (Patient)` : 'Patient';
      }

      const details: string[] = [];
      if (action === 'physician_changed') {
        const toName = item.physician_id
          ? PhysicianUtils.getName(physicians[item.physician_id]) || 'Unknown'
          : 'None';
        if (prev) {
          const fromName = prev.physician_id
            ? PhysicianUtils.getName(physicians[prev.physician_id]) || 'Unknown'
            : 'None';
          details.push(`${fromName} → ${toName}`);
        } else {
          details.push(toName);
        }
      } else if (action === 'details_changed' && prev) {
        if (item.title !== prev.title) details.push(`"${prev.title}" → "${item.title}"`);
        if (item.document_type !== prev.document_type)
          details.push(`${prev.document_type} → ${item.document_type}`);
      }

      // "Moved to another patient" only fits when the previous snapshot had a
      // patient; going from unassigned to assigned is a first assignment.
      const actionLabel =
        action === 'patient_changed' && prev && !prev.patient_id
          ? 'Assigned to patient'
          : DOCUMENT_HISTORY_ACTION_LABELS[action] || action;

      return {
        byWhom,
        date: moment.unix(date!).format('YYYY-MM-DD HH:mm'),
        actionLabel,
        details,
      };
    });
  }

  async getFileUrl(
    getFile: (opts: { errorTitle: string }) => Promise<any>,
    getFileInfo?: () => Promise<any>,
  ): Promise<{ fileUrl: string | undefined; type: string; originalUrl?: string }> {
    let fileUrl: string | undefined, originalUrl: string | undefined;
    let type = 'application/pdf';

    if (!!getFileInfo) {
      try {
        const result = await getFileInfo();
        if (result) {
          let blob = await MiscUtils.urlToBlob(result.url, result.type);
          originalUrl = result.url;
          fileUrl = MiscUtils.getBlobUrl(blob);
          type = blob.type;
          fileUrl = MiscUtils.getBlobUrl(blob);
        }
      } catch (err) {
        console.log(err);
        fileUrl = undefined;
      }
    }

    if (!fileUrl) {
      const result = await getFile({ errorTitle: 'Resource Preview' });
      if (result) {
        type = result.type;
        fileUrl = MiscUtils.getBlobUrl(result);
      }
    }
    return { fileUrl, type, originalUrl };
  }
}

function order(documents: PatientDocument[]): PatientDocument[] {
  return PerformanceUtils.sortBy(documents, ['date']);
}

export default new DocumentUtils();
